/**
 * FctPhaseProgressModal — modal de progreso con lista de fases.
 *
 * Patrón GCvsc §7.2: para procesos backend de duración media (1-2 minutos)
 * donde el progreso se obtiene por polling cada N segundos y consta de fases
 * discretas (cargar datos → validar → procesar → guardar).
 *
 * Características:
 *  - Modal bloqueante, no cerrable mientras está en curso.
 *  - Lista de fases con icono por estado (completada / en curso / pendiente).
 *  - Barra de progreso opcional en fases con conteo detallado.
 *  - Banners de éxito y error con los colores corporativos de progreso.
 *  - Cierre habilitado sólo en estados terminales.
 *
 * El polling lo gestiona el consumidor — este componente sólo recibe el
 * estado actual. La razón es que los endpoints y formatos varían por
 * proceso, y un hook genérico se queda corto.
 *
 * Ejemplo de polling en el consumidor:
 *
 *   const [progreso, setProgreso] = useState<Progreso | null>(null);
 *   const [state, setState] = useState<FctOverlayState>('launching');
 *
 *   useEffect(() => {
 *     if (!open) return;
 *     let cancelled = false;
 *     const poll = async () => {
 *       try {
 *         const p = await getProgreso(recId);
 *         if (cancelled) return;
 *         setProgreso(p);
 *         if (p.estado === 1) setState('completed');
 *         else if (p.estado === 2) setState('error');
 *       } catch (err) {
 *         if (!cancelled) setState('error');
 *       }
 *     };
 *     poll();
 *     const id = setInterval(poll, 2000);
 *     return () => { cancelled = true; clearInterval(id); };
 *   }, [open, recId]);
 *
 *   const fases: FctFaseUI[] = [
 *     { key: 'carga',      label: 'Cargando datos',     completedWhenFase: 1 },
 *     { key: 'relaciones', label: 'Procesando relaciones', completedWhenFase: 2, hasProgress: true },
 *     { key: 'invariantes',label: 'Validando invariantes', completedWhenFase: 3 },
 *     { key: 'guardado',   label: 'Guardando resultados',  completedWhenFase: 4 },
 *   ];
 *
 *   <FctPhaseProgressModal
 *     open={open}
 *     state={state}
 *     fases={fases}
 *     faseActual={progreso?.fase ?? 0}
 *     progressPercent={progreso?.porcentaje}
 *     title="Reconciliación en curso"
 *     successSummary={`Operaciones: ${n} · Excepciones: ${n} · Invariantes: ${n}`}
 *     errorMessage={state === 'error' ? 'No se pudo completar el proceso' : undefined}
 *     onClose={() => setOpen(false)}
 *   />
 */

import React from 'react';
import { Modal, Typography, Space, Progress } from 'antd';
import {
  CheckCircleFilled,
  LoadingOutlined,
  MinusCircleOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import { fctColors } from '../../theme/theme';

const { Title, Text } = Typography;

/** Estados terminales y no terminales del overlay. */
export type FctOverlayState =
  | 'launching'      // disparando la petición inicial
  | 'polling'        // proceso en curso, polling activo
  | 'completed'      // terminado con éxito
  | 'error'          // terminado con error
  | 'launch_failed'; // no se pudo ni siquiera arrancar

/** Definición de una fase de la lista. */
export interface FctFaseUI {
  /** Identificador único de la fase (para la key de React). */
  key: string;
  /** Etiqueta visible. */
  label: string;
  /** Valor mínimo de `faseActual` a partir del cual esta fase se considera completada. */
  completedWhenFase: number;
  /**
   * Si es `true` y la fase está activa, se muestra la barra de Progress con
   * `progressPercent`.
   */
  hasProgress?: boolean;
}

interface FctPhaseProgressModalProps {
  open: boolean;
  state: FctOverlayState;
  /** Título del modal (por defecto: "Proceso en curso"). */
  title?: string;
  /** Lista de fases del proceso, en orden. */
  fases: FctFaseUI[];
  /** Número de fase actual (0 = ninguna, 1 = primera, ...). */
  faseActual: number;
  /** Porcentaje (0-100) para la fase activa que tenga `hasProgress: true`. */
  progressPercent?: number;
  /** Texto del banner de éxito al terminar. */
  successSummary?: React.ReactNode;
  /** Mensaje del banner de error si state === 'error' o 'launch_failed'. */
  errorMessage?: string;
  /** Anchura del modal. Por defecto 520 (convención GCvsc). */
  width?: number;
  /** Callback al cerrar (sólo activo en estados terminales). */
  onClose?: () => void;
}

const renderFaseIcon = (
  fase: FctFaseUI,
  faseActual: number,
  state: FctOverlayState,
) => {
  // Completada
  if (faseActual >= fase.completedWhenFase) {
    return <CheckCircleFilled style={{ color: fctColors.success }} />;
  }
  // Activa: es la fase que está justo "antes" de completar
  const isActive =
    state === 'polling' && faseActual === fase.completedWhenFase - 1;
  if (isActive) {
    return <LoadingOutlined style={{ color: fctColors.faseActivaStroke }} />;
  }
  // Pendiente
  return (
    <MinusCircleOutlined style={{ color: fctColors.fasePendienteIcono }} />
  );
};

export const FctPhaseProgressModal: React.FC<FctPhaseProgressModalProps> = ({
  open,
  state,
  title = 'Proceso en curso',
  fases,
  faseActual,
  progressPercent,
  successSummary,
  errorMessage,
  width = 520,
  onClose,
}) => {
  const isTerminal =
    state === 'completed' || state === 'error' || state === 'launch_failed';

  return (
    <Modal
      open={open}
      closable={isTerminal}
      maskClosable={false}
      footer={null}
      title={<Title level={5} style={{ margin: 0 }}>{title}</Title>}
      width={width}
      onCancel={isTerminal ? onClose : undefined}
    >
      {/* Lista de fases */}
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {fases.map((fase) => {
          const isActive =
            state === 'polling' &&
            faseActual === fase.completedWhenFase - 1;
          return (
            <div key={fase.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ marginTop: 2 }}>
                {renderFaseIcon(fase, faseActual, state)}
              </span>
              <div style={{ flex: 1 }}>
                <Text>{fase.label}</Text>
                {isActive && fase.hasProgress && progressPercent !== undefined && (
                  <Progress
                    percent={progressPercent}
                    size="small"
                    style={{ marginTop: 4, maxWidth: 300 }}
                    strokeColor={fctColors.faseActivaStroke}
                  />
                )}
              </div>
            </div>
          );
        })}
      </Space>

      {/* Banner de éxito */}
      {state === 'completed' && successSummary && (
        <div
          style={{
            marginTop: 16,
            background: fctColors.progresoOkFondo,
            border: `1px solid ${fctColors.progresoOkBorde}`,
            borderRadius: 6,
            padding: '12px 16px',
          }}
        >
          {successSummary}
        </div>
      )}

      {/* Banner de error */}
      {(state === 'error' || state === 'launch_failed') && errorMessage && (
        <div
          style={{
            marginTop: 16,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <CloseCircleFilled
            style={{
              color: fctColors.errorCriticoIcono,
              fontSize: 20,
              marginTop: 4,
            }}
          />
          <div
            style={{
              flex: 1,
              background: fctColors.fondoError,
              borderRadius: 4,
              padding: '8px 12px',
            }}
          >
            <Text type="danger">{errorMessage}</Text>
          </div>
        </div>
      )}
    </Modal>
  );
};
